import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, Typography, Box, IconButton, Chip, Tooltip
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { contractService } from '../services/api';
import SkeletonBlock from './ui/SkeletonBlock';
import EmptyState from './ui/EmptyState';
import { useConfirm } from './Common/ConfirmDialog';
import { useToast } from './Common/ToastProvider';

const ContractList = () => {
  const confirm = useConfirm();
  const toast = useToast();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      const response = await contractService.getAll();
      console.log('Contracts API response:', response);
      setContracts(response || []);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setContracts([]); // Ensure contracts is always an array
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (await confirm({ message: 'Are you sure you want to delete this contract?', danger: true, confirmLabel: 'Delete' })) {
      try {
        await contractService.delete(id);
        fetchContracts();
      } catch (error) {
        console.error('Error deleting contract:', error);
      }
    }
  };

  const handleCreateEmployee = async (contractId) => {
    try {
      await contractService.createEmployeeFromContract(contractId);
      toast('Employee created successfully!', { variant: 'success' });
    } catch (error) {
      toast('Error creating employee: ' + (error.response?.data || error.message), { variant: 'error' });
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getContractTermDisplay = (contract) => {
    if (contract.contractTerm === "неопределено време") {
      const startDate = new Date(contract.startDate);
      const now = new Date();
      const monthsDiff = (now.getFullYear() - startDate.getFullYear()) * 12 + 
                        (now.getMonth() - startDate.getMonth());
      
      if (monthsDiff >= 6) {
        return "постоянен трудов договор";
      } else if (contract.probationPeriodMonths > 0) {
        return "изпитателен срок";
      } else {
        return contract.contractTerm;
      }
    }
    return contract.contractTerm;
  };

  const getTermColor = (contract) => {
    const displayTerm = getContractTermDisplay(contract);
    switch (displayTerm) {
      case 'постоянен трудов договор': return 'success';
      case 'изпитателен срок': return 'warning';
      default: return 'default';
    }
  };

  if (loading) return <SkeletonBlock rows={6} />;

  if (!loading && contracts.length === 0) {
    return <EmptyState title="No contracts" description="There are no contracts yet." actionLabel="Add New Contract" onAction={()=>{ window.history.pushState({}, '', '/contracts/new'); window.dispatchEvent(new PopStateEvent('popstate')); }} />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Contracts</Typography>
        <Button 
          variant="contained" 
          component={Link} 
          to="/contracts/new"
        >
          Add New Contract
        </Button>
      </Box>
      
      <TableContainer component={Paper} sx={{ maxHeight: 560 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Contract #</TableCell>
              <TableCell>Full Name</TableCell>
              <TableCell>Job Title</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>Salary</TableCell>
              <TableCell>Term</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contracts.map((contract) => (
              <TableRow key={contract.id} hover>
                <TableCell>{contract.contractNumber}</TableCell>
                <TableCell>{contract.fullName}</TableCell>
                <TableCell>{contract.jobTitleName}</TableCell>
                <TableCell>{formatDate(contract.startDate)}</TableCell>
                <TableCell>{contract.baseSalary} {contract.salaryCurrency || 'BGN'}{contract.salaryBasis === 'Net' ? ' net' : ''}</TableCell>
                <TableCell>
                  <Chip
                    label={getContractTermDisplay(contract)}
                    size="small"
                    color={getTermColor(contract)}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title="Edit" placement="top"><IconButton aria-label="edit contract" component={Link} to={`/contracts/${contract.id}/edit`}><Edit /></IconButton></Tooltip>
                  <Tooltip title="Delete" placement="top"><IconButton aria-label="delete contract" onClick={() => handleDelete(contract.id)} color="error"><Delete /></IconButton></Tooltip>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleCreateEmployee(contract.id)}
                    sx={{ ml: 1 }}
                  >
                    Create Employee
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ContractList;





